import React, { useState, FC } from 'react';
import Slider from 'react-slick';
import useSWR from 'swr';
import 'slick-carousel/slick/slick.css';
import { css, keyframes } from '@emotion/css';
import { Global } from '@emotion/react';

const InterviewSlider: FC = () => {
  const [active, setActive] = useState(0);

  const settings = {
    infinite: true,
    arrows: true,
    speed: 500,
    variableWidth: true,
    centerMode: true,
    pauseOnHover: false,
    autoplay: true,
    autoplaySpeed: 4000,
    swipe: false,
    beforeChange: (_, next) => {
      setActive(next);
    },
  };

  const { data } = useSWR('./api/index.php');

  const globalStyle = {
    '.slick-arrow': {
      position: 'absolute',
      zIndex: 5,
      textIndent: -9999,
      width: 40,
      height: 80,
      backgroundColor: '#47affe',
      border: 0,
      cursor: 'pointer',
      boxShadow: '0 3px 6px rgba(0, 0, 0, .16)',
      borderRadius: 5,
    },

    '.slick-prev': {
      top: 60,
      left: 0,

      '&:after': {
        content: '""',
        position: 'absolute',
        width: 10,
        height: 10,
        top: '50%',
        left: '50%',
        borderLeft: '3px solid #fff',
        borderTop: '3px solid #fff',
        transform: 'translate(-50%, -50%) translateX(2px) rotate(-45deg)',
      },
    },

    '.slick-next': {
      top: 60,
      right: 0,

      '&:after': {
        content: '""',
        position: 'absolute',
        width: 10,
        height: 10,
        top: '50%',
        left: '50%',
        borderRight: '3px solid #fff',
        borderTop: '3px solid #fff',
        transform: 'translate(-50%, -50%) translateX(-2px) rotate(45deg)',
      },
    },

    '.slick-dots': {
      display: 'flex !important',
      justifyContent: 'center',
      margin: '20px 0 0',

      '& li': {
        margin: '0 5px',

        '& button': {
          textIndent: -9999,
          width: 10,
          height: 10,
          backgroundColor: '#A4A4A4',
          border: 0,
          borderRadius: '50%',
          cursor: 'pointer',
          outline: 0,
        },
      },
    },

    '.slick-active': {
      '& button': {
        backgroundColor: '#333333 !important',
      },
    },
  };

  const bounce = keyframes({
    '0%': {
      transform: 'scale(0)',
    },

    '100%': {
      transform: 'scale(1.8)',
    },
  });

  const bounceSp = keyframes({
    '0%': {
      transform: 'scale(0)',
    },

    '100%': {
      transform: 'scale(1.1)',
    },
  });

  const styles = {
    slide: css({
      padding: '0 10px',
      width: '300px !important',
      height: '168px !important',
    }),

    activeLink: css({
      display: 'block',
      position: 'relative',
      transition: '.3s ease',
      transform: 'scale(1.1)',
      transformOrigin: 'center 70%',
      height: 168,
      overflow: 'hidden',

      '@media (min-width: 768px)': {
        transform: 'scale(1.8)',
      },
    }),

    link: css({
      display: 'block',
      position: 'relative',
      transition: '.3s ease',
      transformOrigin: 'center 70%',
      height: 168,
      overflow: 'hidden',
    }),

    picWrap: css({
      height: 336,
      width: 280,
      overflow: 'hidden',
      borderRadius: '50%',
    }),

    pic: css({
      height: 168,
      width: 280,
      overflow: 'hidden',

      '& img': {
        objectFit: 'cover',
        objectPosition: 'center center',
      },
    }),

    // pic1: css({
    //   '&:before': {
    //     backgroundColor: 'rgba(255, 240, 29, .5)',
    //   },
    // }),

    // pic2: css({
    //   '&:before': {
    //     backgroundColor: 'rgba(71, 255, 219, .5)',
    //   },
    // }),

    balloon: css({
      position: 'absolute',
      width: '100px',
      height: '100px',
      backgroundColor: '#47AFFE',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
      top: -45,
      left: -15,
      animation: `${bounceSp} .25s .5s both`,
      transformOrigin: 'right bottom',
      // transform: 'scale(1.1)',

      // '@media (min-width: 768px)': {
      //   transform: 'scale(1.8)',
      // },

      '@media (min-width: 768px)': {
        animation: `${bounce} .25s .5s both`,
        top: -85,
        left: -40,
      },

      '&:after': {
        content: '""',
        border: '10px solid transparent',
        borderTop: '20px solid #47AFFE',
        position: 'absolute',
        right: '-0px',
        bottom: '-0px',
        transform: 'rotate(-45deg)',
      },
    }),

    balloonPosition: css({
      color: '#fff',
      fontSize: '8px',
      display: 'block',
      textAlign: 'center',
      lineHeight: 1.4,
      position: 'relative',
      width: 75,
      zIndex: 1,
      margin: '0 auto 2px',
    }),

    balloonYear: css({
      color: '#fff',
      fontSize: '8px',
      display: 'block',
      textAlign: 'center',
      lineHeight: 1.4,
      position: 'relative',
      width: 75,
      zIndex: 1,
      margin: '0 auto 2px',
    }),

    balloonName: css({
      color: '#fff',
      fontSize: '13px',
      display: 'block',
      textAlign: 'center',
      position: 'relative',
      zIndex: 1,
    }),

    catch: css({
      position: 'absolute',
      bottom: -30,
      display: 'flex',
      justifyContent: 'center',
      width: '100%',
      left: 0,
    }),

    catchBox: css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }),

    catchText: css({
      backgroundColor: '#47AFFE',
      color: '#fff',
      textAlign: 'center',
      fontSize: '2.5rem',
      fontWeight: 'bold',
      padding: '5px 20px',
      lineHeight: 1.2,
      margin: '0 0 10px',
    }),
  };

  return (
    <div>
      <Global styles={globalStyle} />
      <Slider {...settings}>
        {
          data?.map((elm, index) => {
            if (active === index) {
              return (
                <div className={styles.slide} key={elm.interview}>
                  <a className={styles.activeLink} href={`interview.html?interview=${elm.interview}`}>
                    <div className={styles.picWrap}>
                      <div className={styles.pic}>
                        <img src={`../images/upload/${elm.pic1}`} alt={elm.title} />
                      </div>
                    </div>
                  </a>
                  <div className={styles.balloon}>
                    <div>
                      {
                        elm.position1
                          ? <span className={styles.balloonPosition}>{elm.position1}</span>
                          : null
                      }
                      {
                        elm.year
                          ? <span className={styles.balloonYear}>入社：{elm.year}</span>
                          : null
                      }
                      <span className={styles.balloonName}>{elm.title}</span>
                    </div>
                  </div>
                </div>
              );
            }
            const orderClass = index % 2 === 0 ? styles.pic1 : styles.pic2;
            return (
              <div className={styles.slide} key={elm.interview}>
                <a className={styles.link} href={`interview.html?interview=${elm.interview}`}>
                  <div className={`${styles.picWrap} ${orderClass}`}>
                    <div className={styles.pic}>
                      <img src={`../images/upload/${elm.pic1}`} alt={elm.title} />
                    </div>
                  </div>
                </a>
              </div>
            );
          })
        }
      </Slider>
    </div>
  );
};

export default InterviewSlider;
